/**
 * Stock Sentry Settings
 * Follows Polaris App Settings Layout pattern
 * https://polaris-react.shopify.com/patterns/app-settings-layout
 */

import { useLoaderData, useSubmit, useNavigation, useSearchParams, useBlocker } from 'react-router';
import {
  Page,
  BlockStack,
  Text,
  Banner,
  Box,
  Modal,
} from '@shopify/polaris';
import { QuestionCircleIcon } from '@shopify/polaris-icons';
import { useState, useEffect, useCallback } from 'react';
import { useToastHelpers } from '../components/feedback/ToastProvider';
import { useLoading } from '../hooks/shopify';
import { useSettingsForm, useNotificationChannels, useSettingsToast } from '../hooks/settings';
import type { ShopSettings as SettingsFormData, SettingsFormState } from '../hooks/settings';
import { useTestData } from '../hooks/onboarding';
import {
  NotificationScheduleCard,
  SlackChannelsCard,
  WebhookChannelsCard,
  RecipientListSection,
  VelocitySettingsCard,
  DataExportCard,
  TestDataCard,
  SectionErrorBoundary,
  SettingsSkeleton,
} from '../components/settings';
import { AppBridgeModal } from '../components/app-bridge/AppBridgeModal';
import { useModalRoute } from '../hooks/core/useModalRoute';
import type { SettingsLoaderData } from '../types/settings';
import type { ModalMessage } from '../hooks/core/useModalCommunication';

// Re-export loader and action from server files
export { loader } from '../loaders/settings.server';
export { action } from '../actions/settings.server';

// =============================================================================
// Settings Component
// =============================================================================

export default function Settings() {
  const { settings, notificationChannels, error } = useLoaderData<SettingsLoaderData>();
  const submit = useSubmit();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const toast = useToastHelpers();

  // Channel operations hook - manages all notification channel state and operations
  const {
    channelsByType,
    refreshChannels,
    testChannel,
    toggleChannel,
    deleteChannel,
    deleteRecipient,
    addSlackChannel,
  } = useNotificationChannels(notificationChannels, {
    onSuccess: toast.success,
    onError: toast.error,
  });

  // Destructure channel types for component props
  const { slack: slackChannels, webhook: webhookChannels } = channelsByType;

  // Preserve query parameters for navigation
  const queryString = searchParams.toString();
  const backUrl = `/app${queryString ? `?${queryString}` : ''}`;
  const helpCenterUrl = `/app/help${queryString ? `?${queryString}` : ''}`;

  // Test data management
  const testData = useTestData();
  const [showRemovalModal, setShowRemovalModal] = useState(false);
  
  // Route-based modal for test data removal
  const testRemovalModal = useModalRoute('/app/modal/test-data-removal');

  // Route-based modal for adding webhook channels
  const webhookModal = useModalRoute('/app/modal/add-webhook');

  // Collapsible section state
  const [openSections, setOpenSections] = useState({
    velocitySettings: true,
    dataExport: true,
    testData: false,
  });

  // Toggle section open/closed
  const toggleSection = useCallback((section: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Fetch test data status on mount
  useEffect(() => {
    testData.fetchStatus();
  }, []); // Only run once on mount

  // Handle test data removal
  const handleOpenRemovalModal = useCallback(() => {
    const shop = searchParams.get('shop') || '';
    testRemovalModal.open({
      shop,
      productCount: String(testData.status?.counts?.trackedProducts || 0),
      alertCount: String(testData.status?.counts?.alerts || 0),
      velocityCount: String(testData.status?.counts?.velocityRecords || 0),
    });
    setShowRemovalModal(true);
  }, [searchParams, testData.status, testRemovalModal]);

  const handleCloseRemovalModal = useCallback(() => {
    testRemovalModal.close();
    setShowRemovalModal(false);
  }, [testRemovalModal]);

  const handleConfirmRemoval = useCallback(async () => {
    const success = await testData.deleteTestData();
    if (success) {
      testRemovalModal.close();
      setShowRemovalModal(false);
      toast.success('Test data removed');
      // Navigate back to dashboard to show empty/real state
      window.location.href = backUrl;
    }
  }, [testData, toast, backUrl, testRemovalModal]);

  // Handle messages from test removal modal
  const handleTestRemovalMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'removal-confirmed') {
      handleConfirmRemoval();
    }
  }, [handleConfirmRemoval]);

  // Handle messages from webhook modal
  const handleWebhookMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'webhook-added') {
      refreshChannels();
      webhookModal.close();
      toast.success('Webhook channel added');
    }
  }, [refreshChannels, webhookModal, toast]);

  // Use settings form hook for form state management
  const {
    formData: formState,
    hasChanges,
    updateField,
    reset: resetForm,
    toFormDataPayload,
  } = useSettingsForm(settings as SettingsFormData);

  // Settings toast hook - shows App Bridge toasts when settings are toggled
  const { showSettingChangedToast } = useSettingsToast();

  // Wrapper for updateField that shows toast notifications
  const updateFieldWithToast = useCallback(
    <K extends keyof SettingsFormState>(field: K, value: SettingsFormState[K]) => {
      updateField(field, value);
      showSettingChangedToast(field, value);
    },
    [updateField, showSettingChangedToast]
  );

  // Page loading state
  const isPageLoading = navigation.state === 'loading';
  const isSaving = navigation.state === 'submitting';

  // Control App Bridge loading indicator
  useLoading(isPageLoading || isSaving);

  // Unsaved changes protection - warn before browser navigation or page close
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasChanges) {
        event.preventDefault();
        // Modern browsers show a generic message, but we still need to set returnValue
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Unsaved changes protection - block in-app navigation with React Router
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasChanges && currentLocation.pathname !== nextLocation.pathname
  );

  // Save settings
  const handleSave = useCallback(() => {
    const formData = toFormDataPayload();
    formData.append('intent', 'save');
    submit(formData, { method: 'post' });
  }, [toFormDataPayload, submit]);

  // Discard changes
  const handleDiscard = useCallback(() => {
    resetForm();
  }, [resetForm]);

  // Track previous navigation state to detect form submission completion
  const [lastSubmittedIntent, setLastSubmittedIntent] = useState<string | null>(null);

  // Handle form submission
  useEffect(() => {
    if (navigation.state === 'submitting' && navigation.formData) {
      // Capture the intent when submitting
      const intent = navigation.formData.get('intent');
      setLastSubmittedIntent(intent as string);
    } else if (navigation.state === 'idle' && lastSubmittedIntent) {
      // Show success message when submission completes
      if (lastSubmittedIntent === 'save') {
        toast.success('Settings saved successfully');
      }
      setLastSubmittedIntent(null);
    }
  }, [navigation.state, navigation.formData, lastSubmittedIntent, toast]);

  // Determine primary action based on whether there are changes
  const primaryAction = hasChanges
    ? {
        content: 'Save changes',
        onAction: handleSave,
        loading: isSaving,
      }
    : {
        content: 'Help Center',
        url: helpCenterUrl,
        icon: QuestionCircleIcon,
      };

  // Secondary actions
  const secondaryActions = hasChanges
    ? [
        {
          content: 'Discard',
          onAction: handleDiscard,
        },
      ]
    : undefined;

  // Show skeleton page while loading
  if (isPageLoading) {
    return <SettingsSkeleton />;
  }

  return (
    <>
      {/* Page background - top-right narrow beams */}
      <div className="page-bg-settings" aria-hidden="true" />

      <Page
        title="Settings"
        backAction={{ content: 'Dashboard', url: backUrl }}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
      >
      {error && (
        <Box paddingBlockEnd="400">
          <div className="banner-enter">
            <Banner title="Error loading settings" tone="critical">
              <Text as="p" variant="bodyMd">{error}</Text>
            </Banner>
          </div>
        </Box>
      )}

      <BlockStack gap="500">
        {/* Email Recipients Section - Verified email addresses for notifications */}
        <div className="slide-in-up" style={{ animationDelay: '25ms' }}>
          <SectionErrorBoundary sectionName="Email Recipients">
            <RecipientListSection
              onShowToast={(message, isError) => isError ? toast.error(message) : toast.success(message)}
              onRecipientDelete={deleteRecipient}
            />
          </SectionErrorBoundary>
        </div>

        {/* Notification Schedule - Combined Delivery Settings + Weekly Digest */}
        <div className="slide-in-up" style={{ animationDelay: '35ms' }}>
          <SectionErrorBoundary sectionName="Notification Schedule">
            <NotificationScheduleCard
              timezone={formState.timezone}
              onTimezoneChange={(tz) => updateFieldWithToast('timezone', tz)}
              quietHoursEnabled={formState.quiet_hours_enabled}
              quietHoursStart={formState.quiet_hours_start}
              quietHoursEnd={formState.quiet_hours_end}
              onQuietHoursEnabledChange={(enabled) => updateFieldWithToast('quiet_hours_enabled', enabled)}
              onQuietHoursStartChange={(time) => updateFieldWithToast('quiet_hours_start', time)}
              onQuietHoursEndChange={(time) => updateFieldWithToast('quiet_hours_end', time)}
              weeklyDigestDay={formState.weekly_digest_day}
              weeklyDigestTime={formState.weekly_digest_time}
              weeklyDigestSections={formState.weekly_digest_sections}
              onWeeklyDigestDayChange={(day) => updateField('weekly_digest_day', day as 0 | 1 | 2 | 3 | 4 | 5 | 6)}
              onWeeklyDigestTimeChange={(time) => updateField('weekly_digest_time', time)}
              onWeeklyDigestSectionsChange={(sections) => updateField('weekly_digest_sections', sections as Array<'alerts' | 'top_products' | 'velocity_trends' | 'recommendations'>)}
              shopDomain={settings.shop_domain}
            />
          </SectionErrorBoundary>
        </div>

        {/* Row 2: Slack (left) + Webhooks (right) */}
        <div className="slide-in-up" style={{ animationDelay: '50ms' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--p-space-400)' }}>
            <SectionErrorBoundary sectionName="Slack Channels">
              <SlackChannelsCard
                channels={slackChannels}
                shopDomain={settings.shop_domain}
                onChannelTest={testChannel}
                onChannelToggle={toggleChannel}
                onChannelDelete={deleteChannel}
                onChannelAdd={addSlackChannel}
                onCopySuccess={(message) => toast.success(message)}
                onToast={(message, isError) => isError ? toast.error(message) : toast.success(message)}
                onInstallationsChange={refreshChannels}
                isLoading={isSaving}
              />
            </SectionErrorBoundary>

            <SectionErrorBoundary sectionName="Webhook Channels">
              <WebhookChannelsCard
                channels={webhookChannels}
                onChannelTest={testChannel}
                onChannelToggle={toggleChannel}
                onChannelDelete={deleteChannel}
                onAddWebhookClick={() => webhookModal.open()}
                onCopySuccess={(message) => toast.success(message)}
                isLoading={isSaving}
              />
            </SectionErrorBoundary>
          </div>
        </div>

        {/* Velocity Configuration Section - Lookback period and seasonal learning */}
        <div className="slide-in-up" style={{ animationDelay: '65ms' }}>
          <SectionErrorBoundary sectionName="Velocity Settings">
            <VelocitySettingsCard
              velocityLookbackDays={formState.velocity_lookback_days}
              minimumDataDays={formState.minimum_data_days}
              onLookbackDaysChange={(value) => updateFieldWithToast('velocity_lookback_days', value)}
              onMinimumDataDaysChange={(value) => updateFieldWithToast('minimum_data_days', value)}
              isCollapsible
              isOpen={openSections.velocitySettings}
              onToggle={() => toggleSection('velocitySettings')}
            />
          </SectionErrorBoundary>
        </div>

        {/* Data Export Section - Export format, auto-export, retention */}
        <div className="slide-in-up" style={{ animationDelay: '95ms' }}>
          <SectionErrorBoundary sectionName="Data Export">
            <DataExportCard
              exportFormat={formState.export_format}
              autoExportEnabled={formState.auto_export_enabled}
              autoExportFrequency={formState.auto_export_frequency}
              dataRetentionDays={formState.data_retention_days}
              onExportFormatChange={(value) => updateFieldWithToast('export_format', value)}
              onAutoExportEnabledChange={(enabled) => updateFieldWithToast('auto_export_enabled', enabled)}
              onAutoExportFrequencyChange={(value) => updateFieldWithToast('auto_export_frequency', value)}
              onDataRetentionChange={(value) => updateFieldWithToast('data_retention_days', value)}
              isCollapsible
              isOpen={openSections.dataExport}
              onToggle={() => toggleSection('dataExport')}
            />
          </SectionErrorBoundary>
        </div>

        {/* Test Data Management Section */}
        <div className="slide-in-up" style={{ animationDelay: '110ms' }}>
          <SectionErrorBoundary sectionName="Test Data">
            <TestDataCard
              status={testData.status}
              isDeleting={testData.isDeleting}
              onRemove={handleOpenRemovalModal}
              isCollapsible
              isOpen={openSections.testData}
              onToggle={() => toggleSection('testData')}
            />
          </SectionErrorBoundary>
        </div>
      </BlockStack>

      {/* Unsaved Changes Confirmation Modal */}
      <Modal
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        title="Discard unsaved changes?"
        primaryAction={{
          content: 'Discard changes',
          onAction: () => blocker.proceed?.(),
          destructive: true,
        }}
        secondaryActions={[
          {
            content: 'Keep editing',
            onAction: () => blocker.reset?.(),
          },
        ]}
      >
        <Modal.Section>
          <Text as="p" variant="bodyMd">
            You have unsaved changes that will be lost if you leave this page.
          </Text>
        </Modal.Section>
      </Modal>

      {/* Test Data Removal Confirmation Modal - Route-based */}
      <AppBridgeModal
        src={testRemovalModal.src}
        open={testRemovalModal.isOpen}
        onClose={handleCloseRemovalModal}
        variant="base"
        onMessage={handleTestRemovalMessage}
      />

      {/* Add Webhook Channel Modal - Route-based */}
      <AppBridgeModal
        src={webhookModal.src}
        open={webhookModal.isOpen}
        onClose={webhookModal.close}
        variant="large"
        onMessage={handleWebhookMessage}
      />
    </Page>
    </>
  );
}
